import React from "react";

export async function api<T = any>(path: string, opts: RequestInit & { csrf?: string } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers as Record<string, string>) };
  if (opts.body) headers["Content-Type"] = "application/json";
  if (opts.csrf) headers["x-csrf-token"] = opts.csrf;
  const res = await fetch(path, { ...opts, headers, credentials: "same-origin" });
  const data = await res.json().catch(() => ({ ok: false, error: "Unexpected server response." }));
  return data as T;
}

export function Header({ title }: { title: string }) {
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-5 py-4">
        <a href="/" className="text-lg font-bold text-ink no-underline">
          <span className="text-accent" aria-hidden>✒︎</span> Montgomery Mobile Notary
        </a>
        <span className="text-slate-300" aria-hidden>|</span>
        <h1 className="m-0 text-lg font-semibold text-ink-soft">{title}</h1>
        <a href="tel:+13019154287" className="ml-auto font-semibold text-brand no-underline hover:underline">
          (301) 915-4287
        </a>
      </div>
    </header>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <p role="status" className="my-6 text-center text-ink-soft">
      {label}
    </p>
  );
}

export function ErrorNote({ message }: { message: string }) {
  if (!message) return null;
  return (
    <p role="alert" className="my-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
      {message}
    </p>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-ink">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-ink-soft">{hint}</span> : null}
    </label>
  );
}

export const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-brand focus:outline focus:outline-2 focus:outline-brand/40";

export const btnPrimary =
  "inline-block cursor-pointer rounded-lg bg-brand px-5 py-2.5 font-semibold text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50";

export const btnGhost =
  "inline-block cursor-pointer rounded-lg border-2 border-brand bg-transparent px-5 py-2 font-semibold text-brand hover:bg-brand/5 disabled:opacity-50";

/* ------------------------------------------------------------------ */
/* Calendar date picker                                                */
/* ------------------------------------------------------------------ */

/** Today's calendar date in America/New_York (YYYY-MM-DD). */
export function easternToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
}

function addDaysStr(date: string, n: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1 + n, 1)).toISOString().slice(0, 7);
}

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(y!, m! - 1, 1)),
  );
}

/** "Tuesday, June 16" for a YYYY-MM-DD calendar date. */
export function friendlyDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(y!, m! - 1, d!)),
  );
}

const WEEKDAY_HEADS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/**
 * Month-grid date picker fed by /api/availability-month. Days with no open
 * slots are disabled; the selected day stays highlighted so it's one click
 * to change. Booking window: today .. today + 60 days (Eastern).
 */
export function DatePicker({
  serviceSlug,
  value,
  onChange,
}: {
  serviceSlug: string;
  value: string;
  onChange: (date: string) => void;
}) {
  const today = React.useMemo(easternToday, []);
  const maxDate = React.useMemo(() => addDaysStr(today, 60), [today]);
  const minMonth = today.slice(0, 7);
  const maxMonth = maxDate.slice(0, 7);
  const [month, setMonth] = React.useState(value ? value.slice(0, 7) : minMonth);
  const [days, setDays] = React.useState<Record<string, number> | null>(null);

  React.useEffect(() => {
    let live = true;
    setDays(null);
    api<{ ok: boolean; days?: Record<string, number> }>(
      `/api/availability-month?service=${encodeURIComponent(serviceSlug)}&month=${month}`,
    ).then((r) => {
      if (live) setDays(r.ok ? (r.days ?? {}) : {});
    });
    return () => {
      live = false;
    };
  }, [serviceSlug, month]);

  const [y, m] = month.split("-").map(Number);
  const firstWeekday = new Date(Date.UTC(y!, m! - 1, 1)).getUTCDay();
  const numDays = new Date(Date.UTC(y!, m!, 0)).getUTCDate();

  const cells: (string | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: numDays }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`),
  ];

  return (
    <div className="w-full max-w-xs rounded-xl border border-amber-900/15 bg-white p-3 shadow-sm" role="group" aria-label="Choose a date">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          className="cursor-pointer rounded-lg border border-amber-900/20 bg-transparent px-2.5 py-1 font-bold text-brand disabled:cursor-default disabled:opacity-30"
          disabled={month <= minMonth}
          aria-label="Previous month"
          onClick={() => setMonth(addMonths(month, -1))}
        >
          ‹
        </button>
        <span className="font-bold" style={{ fontFamily: "Fraunces, Georgia, serif" }} aria-live="polite">
          {monthLabel(month)}
        </span>
        <button
          type="button"
          className="cursor-pointer rounded-lg border border-amber-900/20 bg-transparent px-2.5 py-1 font-bold text-brand disabled:cursor-default disabled:opacity-30"
          disabled={month >= maxMonth}
          aria-label="Next month"
          onClick={() => setMonth(addMonths(month, 1))}
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAY_HEADS.map((w) => (
          <span key={w} className="py-1 text-xs font-bold text-ink-soft" aria-hidden>
            {w}
          </span>
        ))}
        {cells.map((date, i) =>
          date === null ? (
            <span key={`pad-${i}`} />
          ) : (
            (() => {
              const inWindow = date >= today && date <= maxDate;
              const open = (days?.[date] ?? 0) > 0;
              const selectable = inWindow && open && days !== null;
              const selected = value === date;
              return (
                <button
                  key={date}
                  type="button"
                  disabled={!selectable}
                  aria-pressed={selected}
                  aria-label={`${friendlyDate(date)}${selectable ? `, ${days![date]} times open` : ", unavailable"}`}
                  onClick={() => onChange(date)}
                  className={
                    "aspect-square cursor-pointer rounded-lg border text-sm font-semibold transition-colors " +
                    (selected
                      ? "border-accent bg-accent text-white"
                      : selectable
                        ? "border-transparent bg-paper-alt text-ink hover:border-accent"
                        : "cursor-default border-transparent bg-transparent text-ink-soft/35 line-through decoration-transparent")
                  }
                >
                  {Number(date.slice(8))}
                </button>
              );
            })()
          ),
        )}
      </div>
      <p className="mb-0 mt-2 text-center text-xs text-ink-soft" role="status">
        {days === null ? "Checking the calendar…" : "Grayed-out days have no openings."}
      </p>
    </div>
  );
}
